import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { TrendingUp, AlertTriangle, FileDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard({ cfg }) {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/admin/stats`, cfg())
      .then((r) => setStats(r.data))
      .catch(() => toast.error("Couldn't read the omens."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!stats) return <p className="font-type text-sm text-paper/50 py-10 text-center">Reading the entrails...</p>;

  const downloadTaxCsv = async () => {
    try {
      const r = await axios.get(`${API}/admin/tax-report.csv`, { ...cfg(), responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "atrocity-tax-report.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Tax report failed to rise.");
    }
  };

  const cards = [
    { label: "TOTAL REVENUE", value: `$${stats.revenue_total.toFixed(2)}`, testId: "stat-revenue-total" },
    { label: "LAST 7 DAYS", value: `$${stats.revenue_7d.toFixed(2)}`, testId: "stat-revenue-7d" },
    { label: "LAST 30 DAYS", value: `$${stats.revenue_30d.toFixed(2)}`, testId: "stat-revenue-30d" },
    { label: "PAID ORDERS", value: stats.orders_count, testId: "stat-orders" },
  ];

  return (
    <div data-testid="admin-dashboard-panel">
      {stats.low_stock.length > 0 && (
        <div data-testid="low-stock-banner" className="border-2 border-crimson bg-blooddeep/20 p-4 mb-8 flex items-start gap-3">
          <AlertTriangle size={18} className="text-crimson shrink-0 mt-0.5" />
          <div>
            <p className="font-impact uppercase tracking-wider text-xs text-crimson mb-1">The rack runs dry</p>
            <p className="font-type text-xs text-paper/70">
              {stats.low_stock.map((p) => `${p.title} (${p.stock} left)`).join(" — ")}
            </p>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-6">
        <p className="font-type text-sm text-paper/60">The money, the movers, and what the taxman needs.</p>
        <button
          data-testid="tax-csv-button"
          onClick={downloadTaxCsv}
          className="flex items-center gap-2 font-impact uppercase tracking-wider text-xs bg-blooddeep text-white border-2 border-black px-4 py-2.5 ink-shadow-sm transition-colors duration-150 hover:bg-crimson"
        >
          <FileDown size={14} /> Tax Report CSV
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        {cards.map((c) => (
          <div key={c.label} className="border border-blooddeep/50 bg-pulp p-5">
            <p className="font-courier text-[10px] uppercase tracking-widest text-paper/40 mb-2">{c.label}</p>
            <p data-testid={c.testId} className="font-display text-2xl sm:text-3xl text-crimson crimson-glow">{c.value}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        <div className="border border-blooddeep/50 bg-pulp p-6">
          <h3 className="font-impact uppercase tracking-wider text-sm text-paper mb-4 flex items-center gap-2">
            <TrendingUp size={15} className="text-bloodglow" /> Best Sellers
          </h3>
          {stats.top_products.length === 0 ? (
            <p className="font-type text-xs text-paper/50">No sales yet. The crypt awaits its first victim.</p>
          ) : (
            <div className="space-y-3">
              {stats.top_products.map((p, i) => (
                <div key={p.title} data-testid={`top-product-${i}`} className="flex items-center justify-between gap-3">
                  <span className="font-type text-xs text-paper/70 truncate">
                    <span className="text-bloodglow font-impact mr-2">{i + 1}.</span>
                    {p.title} <span className="text-paper/40">× {p.units}</span>
                  </span>
                  <span className="font-marker text-yell text-sm shrink-0">${p.revenue.toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}
          <div className="mt-6 pt-4 border-t border-blooddeep/40 flex justify-between font-courier text-[11px] uppercase tracking-widest text-paper/50">
            <span data-testid="stat-digital-split">Digital ${stats.digital_revenue.toFixed(2)}</span>
            <span data-testid="stat-physical-split">Physical ${stats.physical_revenue.toFixed(2)}</span>
          </div>
        </div>

        <div className="border border-blooddeep/50 bg-pulp p-6">
          <h3 className="font-impact uppercase tracking-wider text-sm text-paper mb-4">Latest Blood</h3>
          {stats.recent_orders.length === 0 ? (
            <p className="font-type text-xs text-paper/50">Nothing yet.</p>
          ) : (
            <div className="space-y-3">
              {stats.recent_orders.map((o) => (
                <div key={o.id} data-testid={`recent-order-${o.id}`} className="flex items-center justify-between gap-3">
                  <span className="font-type text-xs text-paper/70 truncate">{o.customer_name}</span>
                  <span className="font-courier text-[11px] text-paper/40">{o.status}</span>
                  <span className="font-marker text-yell text-sm">${o.total.toFixed(2)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
